
import React from 'react';
import { Share2, Bookmark, BookmarkCheck, Languages } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { LANGUAGE_CODES, SupportedLanguage } from '@/hooks/useTranslation';
import ArticleShareOptions from '@/components/article/ArticleShareOptions';
import AudioPlayer from '@/components/article/AudioPlayer';
import { useAuth } from '@/hooks/useAuth';

interface ArticleToolbarProps {
  articleId: string;
  hasAudio: boolean;
  hasTranslations: boolean;
  currentLanguage: SupportedLanguage;
  setCurrentLanguage: (language: SupportedLanguage) => void;
  isBookmarked?: boolean;
  toggleBookmark?: () => Promise<void>;
}

const ArticleToolbar: React.FC<ArticleToolbarProps> = ({
  articleId,
  hasAudio,
  hasTranslations,
  currentLanguage,
  setCurrentLanguage,
  isBookmarked = false,
  toggleBookmark
}) => {
  const { user } = useAuth();
  
  const handleShare = () => {
    toast({
      title: "Link copied to clipboard",
      description: "You can now share this article with others.",
    });
  };

  const handleLanguageChange = (language: SupportedLanguage) => {
    setCurrentLanguage(language);
    toast({
      title: `Article translated to ${language}`,
      description: "Translation powered by DeepL",
    });
  };

  // List of supported languages for the toolbar
  const supportedLanguages = Object.keys(LANGUAGE_CODES) as SupportedLanguage[];

  return (
    <div className="border-y border-border py-4 mb-8 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm">
              <Share2 className="h-4 w-4 mr-1" />
              Share
            </Button>
          </DialogTrigger>
          <DialogContent>
            <ArticleShareOptions articleId={articleId} onShare={handleShare} />
          </DialogContent>
        </Dialog>
        
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={toggleBookmark}
          className={isBookmarked ? "text-primary" : ""}
          disabled={!user || !toggleBookmark}
        >
          {isBookmarked ? (
            <>
              <BookmarkCheck className="h-4 w-4 mr-1" />
              Saved
            </>
          ) : (
            <>
              <Bookmark className="h-4 w-4 mr-1" />
              Save
            </>
          )}
        </Button>
        
        {hasAudio && (
          <AudioPlayer articleId={articleId} />
        )}
        
        {hasTranslations && (
          <Drawer>
            <DrawerTrigger asChild>
              <Button variant="ghost" size="sm">
                <Languages className="h-4 w-4 mr-1" />
                Translate
              </Button>
            </DrawerTrigger>
            <DrawerContent>
              <div className="p-4 max-w-md mx-auto">
                <h3 className="text-lg font-medium mb-4">Translate Article</h3>
                <Tabs defaultValue="select" className="w-full">
                  <TabsList className="grid grid-cols-2 mb-4">
                    <TabsTrigger value="auto">Auto-Detect</TabsTrigger>
                    <TabsTrigger value="select">Select Language</TabsTrigger>
                  </TabsList>
                  <TabsContent value="auto">
                    <p className="text-sm text-muted-foreground mb-4">
                      We'll automatically detect the best language for you based on your preferences.
                    </p>
                  </TabsContent>
                  <TabsContent value="select">
                    <div className="grid grid-cols-2 gap-2">
                      {supportedLanguages.map(lang => (
                        <Button 
                          key={lang}
                          variant={currentLanguage === lang ? "default" : "outline"}
                          className="justify-start"
                          onClick={() => handleLanguageChange(lang)}
                        >
                          {lang}
                        </Button>
                      ))}
                    </div>
                  </TabsContent>
                </Tabs>
                <p className="text-xs text-muted-foreground mt-4 text-center">
                  Translation powered by DeepL
                </p>
              </div>
            </DrawerContent>
          </Drawer>
        )}
      </div>
    </div>
  );
};

export default ArticleToolbar;
